import { InputNumberProps, InputProps } from "antd";
import React from "react";
import { Label, Input as FInput, Text, InputNumber } from "./styles/style";
import {
  FieldError,
  RequiredMark,
  ScreenReaderOnly,
} from "src/components/Form/styles/form";
import { useIsRequired } from "src/components/Form/requiredFields";

interface FInputProps extends InputProps {
  id: string;
  error: string;
  label: React.ReactNode;
  onChange?: (e) => void;
  /** Overrides the surrounding form's schema. Usually unnecessary. */
  required?: boolean;
}

const Input = ({
  id,
  error,
  label,
  required,
  onChange,
  ...rest
}: FInputProps) => {
  /**
   * The mark says REQUIRED, and it is one character.
   *
   * This field used to print "(optional)" after the labels of the fields that
   * were not required — the opposite convention to the product's other two
   * field components, and nine characters where a `*` is one. On a two-up
   * form it wrapped: "Routing / ABA / Fedwire code (optional)" took two lines
   * in a 193px cell and pushed its own input twenty pixels below the field
   * beside it, so the row stopped being a row.
   */
  const isRequired = required ?? useIsRequired((rest as any).name);

  return (
    <>
      <Label htmlFor={id}>
        <Text>
          {label}
          {isRequired && (
            <>
              <RequiredMark aria-hidden="true">*</RequiredMark>
              <ScreenReaderOnly> required</ScreenReaderOnly>
            </>
          )}
        </Text>
      </Label>
      {/* No `type="danger"` and no `color`: the first landed on the DOM as an
          input TYPE, the second only fed the accent border this field no
          longer draws. The invalid state is announced, and the complaint goes
          under the field like every other error in the product. */}
      <FInput
        size="large"
        onChange={onChange}
        aria-invalid={error ? true : undefined}
        aria-required={isRequired || undefined}
        {...rest}
      />
      {error && <FieldError role="alert">{error}</FieldError>}
    </>
  );
};
interface FInputNumberProps extends InputNumberProps {
  name: string;
  label?: string;
  error?: string;
  /** Marks the label, so a field that must be filled in says so. */
  required?: boolean;
}

Input.InputNumber = function DepositInputNumber({
  onChange,
  defaultValue,
  label,
  formatter,
  parser,
  name,
  error,
  id,
  required,
  ...rest
}: FInputNumberProps) {
  const isRequired = required ?? useIsRequired(name);

  return (
    <>
      <Label htmlFor={id}>
        {/* The name stays ink whatever the state of the field: it names the
            field, it is not the complaint. */}
        <Text>
          {label}
          {isRequired && (
            <>
              <RequiredMark aria-hidden="true">*</RequiredMark>
              <ScreenReaderOnly> required</ScreenReaderOnly>
            </>
          )}
        </Text>
      </Label>
      <InputNumber
        id={id}
        name={name}
        aria-required={isRequired || undefined}
        defaultValue={defaultValue}
        size="large"
        formatter={formatter}
        parser={parser}
        error={error}
        onChange={onChange}
        aria-invalid={error ? true : undefined}
        {...rest}
      />
      {error && <FieldError role="alert">{error}</FieldError>}
    </>
  );
};

export default Input;
